import { useEffect, useState } from 'react';
import { MESSAGING_ENABLED } from '../config/features';
import { useNavigate } from 'react-router-dom';
import { C, t } from '../constants/dashboardConstants';
import { canSendMessage, startConversation, isMessagingUnavailable } from '../../api/messageApi';
import { SEND_DENY_MESSAGE } from './messageUtils';

/**
 * 프로필에서 대화를 시작하는 버튼.
 *
 * 눌러본 뒤 400을 받는 대신 can-send로 미리 판정해 사유와 함께 막는다.
 * targetUserId가 없으면 아무것도 렌더하지 않는다 — 계정 ID 없이는 대화를 열 수 없다.
 */
export default function StartMessageButton({
  targetUserId, castingPostId, targetLabel = '상대', messagesPath = '/talent/messages',
}) {
  // 런칭 전에는 대화를 시작할 수 없다
  const messagingOff = !MESSAGING_ENABLED;
  const navigate = useNavigate();
  const [permission, setPermission] = useState(null); // null = 확인 중
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (messagingOff || targetUserId == null) return;
    let cancelled = false;
    canSendMessage(targetUserId)
      .then(p => { if (!cancelled) setPermission(p); })
      .catch(err => {
        // 백엔드 미배포면 버튼을 감춘다 (보내기를 시도해도 실패할 뿐이므로)
        if (!cancelled) setPermission(isMessagingUnavailable(err) ? { allowed: false, hidden: true } : { allowed: true });
      });
    return () => { cancelled = true; };
  }, [messagingOff, targetUserId]);

  if (messagingOff || targetUserId == null || permission?.hidden) return null;

  const denied = permission && !permission.allowed;
  const deniedText = denied
    ? (SEND_DENY_MESSAGE[permission.reason] ?? '메시지를 보낼 수 없습니다.')
    : null;

  const handleSend = async () => {
    const text = body.trim();
    if (!text || sending) return;
    setSending(true);
    setError(null);
    try {
      await startConversation({ targetUserId, castingPostId, body: text });
      setOpen(false);
      setBody('');
      navigate(messagesPath);
    } catch (err) {
      const reason = err?.response?.data?.reason;
      setError(SEND_DENY_MESSAGE[reason] ?? '메시지를 보내지 못했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        onClick={() => !denied && setOpen(true)}
        disabled={denied || permission == null}
        title={deniedText ?? undefined}
        style={{
          flex: 1, height: '52px', borderRadius: '14px', border: `1px solid ${C.g200}`,
          background: C.white, cursor: denied ? 'not-allowed' : 'pointer',
          opacity: denied || permission == null ? 0.5 : 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          ...t('15px', 600, C.bk),
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"
            stroke={C.bk} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        메시지 보내기
      </button>

      {denied && (
        <span style={{ ...t('13px', 400, C.sub), flexBasis: '100%' }}>{deniedText}</span>
      )}

      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 99999,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: '520px', background: C.white, borderRadius: '20px',
              padding: '28px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '16px',
            }}
          >
            <span style={t('20px', 700, C.bk)}>{targetLabel}에게 메시지</span>
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              placeholder="첫 메시지를 입력하세요."
              aria-label="첫 메시지"
              style={{
                minHeight: '140px', padding: '14px', resize: 'vertical',
                border: `1px solid ${C.g200}`, borderRadius: '12px', outline: 'none',
                fontFamily: 'Pretendard, sans-serif', fontSize: '15px', boxSizing: 'border-box',
              }}
            />
            {error && <span style={t('13px', 500, '#EF4444')}>{error}</span>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                onClick={() => setOpen(false)}
                style={{ padding: '12px 20px', borderRadius: '10px', border: 'none', background: 'none', cursor: 'pointer', ...t('14px', 500, C.sub) }}
              >
                취소
              </button>
              <button
                onClick={handleSend}
                disabled={!body.trim() || sending}
                style={{
                  padding: '12px 20px', borderRadius: '10px', border: 'none',
                  background: body.trim() && !sending ? C.bk : C.g200,
                  cursor: body.trim() && !sending ? 'pointer' : 'default',
                  ...t('14px', 600, C.white),
                }}
              >
                {sending ? '보내는 중...' : '보내기'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
